import React, { Dispatch, SetStateAction, useEffect } from 'react';
import * as S from '../RegisterAcademicProject/styles';
import QuillEditor from '../../../../../../../../InputTextQuill';
import { AlertWarning } from '../../../../../../../../Alert';
import { IFile } from '../RegisterAcademicProject';
import FieldFormik from '../../../../../../../../FieldFormik';
import { ISubjectContentData } from '../../../../../..';
import DownloadIcon from '@mui/icons-material/Download';
import ButtonIcon from '../../../../../../../../Buttons/ButtonIcon';
import {
  AcademicProjectKind,
  academicProjectLabel,
} from '../../../../../../../../../../util/newStudent/academicProject';

interface Props {
  student?: string;
  date: string;
  projectKind: AcademicProjectKind;
  type: number;
  subject?: string;
  textObservation: string;
  setTextObservation: Dispatch<SetStateAction<string>>;
  finalReport: IFile;
  setFinalReport: Dispatch<SetStateAction<IFile>>;
  mandatoryAttachment: IFile;
  setMandatoryAttachment: Dispatch<SetStateAction<IFile>>;
  fileTcc: IFile;
  setFileTcc: Dispatch<SetStateAction<IFile>>;
  setProjectTitle: Dispatch<SetStateAction<string>>;
  info: ISubjectContentData;
}

export default function InfoListAcademicProject({
  student,
  date,
  subject,
  textObservation,
  setTextObservation,
  finalReport,
  setFinalReport,
  mandatoryAttachment,
  setMandatoryAttachment,
  projectKind,
  setProjectTitle,
  fileTcc,
  setFileTcc,
  type,
  info,
}: Props) {
  const isTcc = projectKind === 'tcc';
  const isAssessmentWork = projectKind === 'assessmentWork';
  const isEvaluativeActivity = projectKind === 'evaluativeActivity';
  const projectLabel = academicProjectLabel(
    projectKind,
    info.purposeText,
    info.title,
  );

  const handleFirstItemFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setMandatoryAttachment({
        file: file,
        name: file.name,
      });
    } else {
      event.target.value = '';
      setMandatoryAttachment({
        file: undefined,
        name: '',
      });
    }
  };

  const handleFileChangeTcc = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileTcc({
        file: file,
        name: file.name,
      });
    } else {
      event.target.value = '';
      setFileTcc({
        file: undefined,
        name: '',
      });
    }
  };

  const handleSecondItemFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setFinalReport({
        file: file,
        name: file.name,
      });
    } else {
      event.target.value = '';
      setFinalReport({
        file: undefined,
        name: '',
      });
    }
  };

  useEffect(() => {
    setTextObservation('');
    setMandatoryAttachment({
      file: undefined,
      name: '',
    });
    setFinalReport({
      file: undefined,
      name: '',
    });
    setFileTcc({
      file: undefined,
      name: '',
    });
  }, []);

  if (isAssessmentWork) {
    return (
      <>
        <S.Key>{info.title}</S.Key>
        <S.ContainerFile>
          <S.Text>
            <S.Text>Faça o download do arquivo</S.Text>
            <ButtonIcon
              color="success"
              icon={<DownloadIcon fontSize="large" />}
              onClick={() => window.open(info.url)}
            />
          </S.Text>
        </S.ContainerFile>

        <S.Text>Entregar Trabalho</S.Text>
        <S.ContainerFile>
          <S.InputFile>
            <S.SpanFile>
              {mandatoryAttachment.name
                ? 'Substituir arquivo'
                : 'Escolher arquivo'}
            </S.SpanFile>
            <S.ContainerInputFile
              type="file"
              className="input-file-first-item"
              onChange={handleFirstItemFileChange}
            />
          </S.InputFile>
          <S.Text>
            {mandatoryAttachment.name
              ? mandatoryAttachment.name
              : 'Nenhum arquivo escolhido.'}
          </S.Text>
        </S.ContainerFile>
        <S.Key>Observação:</S.Key>
        <QuillEditor setText={setTextObservation} text={textObservation} />
      </>
    );
  }

  if (isEvaluativeActivity) {
    return (
      <>
        <S.Key>{projectLabel}</S.Key>
        <FieldFormik
          name="projectTitle"
          onChange={({ target }) => {
            setProjectTitle(target.value);
          }}
          type="string"
          label="Título do Trabalho"
          widthSize="fullWidth"
          notLimitHeight
        />
        <S.Text>Entregar arquivo (PDF)</S.Text>
        <S.ContainerFile>
          <S.InputFile>
            <S.SpanFile>
              {mandatoryAttachment.name
                ? 'Substituir arquivo'
                : 'Escolher arquivo'}
            </S.SpanFile>
            <S.ContainerInputFile
              type="file"
              accept="application/pdf,.pdf"
              className="input-file-first-item"
              onChange={handleFirstItemFileChange}
            />
          </S.InputFile>
          <S.Text>
            {mandatoryAttachment.name
              ? mandatoryAttachment.name
              : 'Nenhum arquivo escolhido.'}
          </S.Text>
        </S.ContainerFile>
        <S.Key>Observação:</S.Key>
        <QuillEditor setText={setTextObservation} text={textObservation} />
        <AlertWarning title="Arquivos (Tamanho máximo de upload: 10Mb). Somente PDF." />
      </>
    );
  }

  return (
    <S.ContainerInfo>
      {isTcc ? (
        type !== 3 && (
          <>
            <FieldFormik
              name="finalReport"
              onChange={({ target }) => {
                setProjectTitle(target.value);
              }}
              type="string"
              label="Título do TCC (Sem abreviações - O Título informado constará no certificado) "
              widthSize="fullWidth"
              notLimitHeight
            />
          </>
        )
      ) : (
        <>
          <S.KeyValue>
            <S.Key>Aluno: </S.Key> <S.Value> {student}</S.Value>
          </S.KeyValue>
          <S.KeyValue>
            <S.Key>Entrada: </S.Key> <S.Value> {date}</S.Value>
          </S.KeyValue>
          <S.KeyValue>
            <S.Key>Tema: </S.Key>{' '}
            <S.Value> {subject ?? projectLabel}</S.Value>
          </S.KeyValue>
        </>
      )}
      <S.Key>{type !== 3 ? 'Observação: ' : 'Justificativa: '}</S.Key>
      <QuillEditor setText={setTextObservation} text={textObservation} />

      {!isTcc ? (
        <>
          <S.Key>
            {type !== 8 ? 'Documentação Obrigatória' : 'Declaração '}
          </S.Key>
          <S.ContainerFile>
            <S.InputFile>
              <S.SpanFile>
                {mandatoryAttachment.name
                  ? 'Substituir arquivo'
                  : 'Escolher arquivo'}
              </S.SpanFile>
              <S.ContainerInputFile
                type="file"
                className="input-file-first-item"
                onChange={handleFirstItemFileChange}
              />
            </S.InputFile>
            <S.Text>
              {mandatoryAttachment.name
                ? mandatoryAttachment.name
                : 'Nenhum arquivo escolhido.'}
            </S.Text>
          </S.ContainerFile>
          <S.Key>
            {type !== 8
              ? 'Relatório Final'
              : 'Contracheque / Contagem de Tempo / Contrato'}{' '}
          </S.Key>
          <S.ContainerFile>
            <S.InputFile>
              <S.SpanFile>
                {finalReport.name ? 'Substituir arquivo' : 'Escolher arquivo'}
              </S.SpanFile>
              <S.ContainerInputFile
                type="file"
                className="input-file-second-item"
                onChange={handleSecondItemFileChange}
              />
            </S.InputFile>

            <S.Text>
              {finalReport.name
                ? finalReport.name
                : 'Nenhum arquivo escolhido.'}
            </S.Text>
          </S.ContainerFile>
        </>
      ) : (
        type !== 3 && (
          <>
            <S.Key>Arquivo: </S.Key>
            <S.ContainerFile>
              <S.InputFile>
                <S.SpanFile>
                  {fileTcc.name ? 'Substituir arquivo' : 'Escolher arquivo'}
                </S.SpanFile>
                <S.ContainerInputFile
                  type="file"
                  className="input-file-first-item"
                  onChange={handleFileChangeTcc}
                />
              </S.InputFile>
              <S.Text>
                {fileTcc.name ? fileTcc.name : 'Nenhum arquivo escolhido.'}
              </S.Text>
            </S.ContainerFile>
          </>
        )
      )}
      {type !== 3 && (
        <AlertWarning title="Arquivos (Tamanho máximo de upload: 8Mb)." />
      )}
    </S.ContainerInfo>
  );
}
